/**
 * Shared InReview flag presentation — label + chip colors (Phase 11).
 * Used by the queue (Task 11.1) and the review detail panel (Task 11.2).
 * Colors per the Task 11.1 spec: GPS amber, dispatcher blue, volume/gauge red,
 * signature/duplicate grey.
 */

import * as React from 'react';
import type { InReviewFlagCode } from '@lodex-pro/shared';

export interface FlagMeta {
  readonly label: string;
  readonly bg: string;
  readonly fg: string;
}

export const FLAG_META: Record<InReviewFlagCode, FlagMeta> = {
  GPS_OUTSIDE_RANGE: {
    label: 'GPS Outside Range',
    bg: 'rgba(246, 182, 94, 0.18)',
    fg: 'var(--color-warning)',
  },
  // DEC-319 (d) — amber like its GPS sibling: a pickup arrival nobody could check.
  GPS_UNVERIFIABLE: {
    label: 'GPS Unverifiable',
    bg: 'rgba(246, 182, 94, 0.18)',
    fg: 'var(--color-warning)',
  },
  DISPATCHER_COMPLETED: {
    label: 'Dispatcher Completed',
    bg: 'rgba(123, 150, 232, 0.18)',
    fg: 'var(--color-link)',
  },
  VOLUME_ANOMALY: {
    label: 'Volume Anomaly',
    bg: 'rgba(243, 139, 139, 0.18)',
    fg: 'var(--color-danger)',
  },
  MISSING_GAUGE: {
    label: 'Missing Gauge',
    bg: 'rgba(243, 139, 139, 0.18)',
    fg: 'var(--color-danger)',
  },
  MISSING_SIGNATURE: {
    label: 'Missing Signature',
    bg: 'rgba(125, 130, 142, 0.18)',
    fg: 'var(--color-text-secondary)',
  },
  // OPEN-121 — a REQUIRED-photo job type with no photo, a skip reason, or no photo report.
  // Amber: check the photos (or the reason); informational, not ack-required.
  MISSING_PHOTO: {
    label: 'Missing Photo',
    bg: 'rgba(246, 182, 94, 0.18)',
    fg: 'var(--color-warning)',
  },
  DUPLICATE_TIMESTAMPS: {
    label: 'Duplicate Timestamps',
    bg: 'rgba(125, 130, 142, 0.18)',
    fg: 'var(--color-text-secondary)',
  },
  // Phase 11 / Task 11.4 (DEC-125) — set when the reviewed net BBL is edited. Blue
  // (an intentional review action, not an anomaly).
  BILLING_FIELD_EDITED: {
    label: 'Billing Field Edited',
    bg: 'rgba(123, 150, 232, 0.18)',
    fg: 'var(--color-link)',
  },
  // Phase 11 / Task 11.5 (DEC-126) — escalated to tenant admin. Red (needs attention).
  ESCALATED: { label: 'Escalated', bg: 'rgba(243, 139, 139, 0.18)', fg: 'var(--color-danger)' },
  // OPEN-039 — real work, no rate: the ticket was routed here instead of skipping
  // review, because skipping would have made it permanently unbillable. Amber: it
  // needs a decision (set a rate, or approve as non-billable), not a correction.
  MISSING_RATE: {
    label: 'Missing Rate',
    bg: 'rgba(246, 182, 94, 0.18)',
    fg: 'var(--color-warning)',
  },
  // DEC-321 / OPEN-082 — dispatch forced a stuck job into review. Red: the ticket was never
  // finished by the driver, and it must be acknowledged before it can be approved.
  FORCED_BY_DISPATCH: {
    label: 'Forced by Dispatch',
    bg: 'rgba(243, 139, 139, 0.18)',
    fg: 'var(--color-danger)',
  },
  // DEC-352 / OPEN-155 — a record arrived with a time the server could not trust (wrong
  // phone clock, or sent long after it happened). Amber: check the times; informational.
  TIME_CHECK: { label: 'Time Check', bg: 'rgba(246, 182, 94, 0.18)', fg: 'var(--color-warning)' },
  // DEC-357 / OPEN-144 — a second run ticket is parked. Amber: it needs a decision (keep the
  // saved ticket or use the parked one), and Approve is blocked until someone makes it.
  PARKED_CAPTURE: {
    label: 'Second Ticket Parked',
    bg: 'rgba(246, 182, 94, 0.18)',
    fg: 'var(--color-warning)',
  },
  // #52 / DEC-358 — no customer disposal fee for this site and day. Amber like Missing Rate:
  // it needs a decision (set the fee, or approve without it), not a correction.
  MISSING_DISPOSAL_FEE: {
    label: 'Missing Disposal Fee',
    bg: 'rgba(246, 182, 94, 0.18)',
    fg: 'var(--color-warning)',
  },
  // Bug 2 (Corbin) — no actual disposal on a disposal-ending job. Red: approve is blocked until
  // the office records where the water actually went.
  DISPOSAL_NOT_RECORDED: {
    label: 'Disposal Not Recorded',
    bg: 'rgba(243, 139, 139, 0.15)',
    fg: 'var(--color-danger)',
  },
  // OPEN-170 — live: photos claimed, none received 24 hours on. Amber like Missing Photo.
  PHOTO_NOT_RECEIVED: {
    label: 'Photo Not Received',
    bg: 'rgba(246, 182, 94, 0.18)',
    fg: 'var(--color-warning)',
  },
  // Firestone gap 10 — no truck on the load, so no QuickBooks Class. Amber like Missing Photo:
  // it needs a check, and approve is not blocked.
  MISSING_TRUCK: {
    label: 'No Truck',
    bg: 'rgba(246, 182, 94, 0.18)',
    fg: 'var(--color-warning)',
  },
  // OPEN-217 (DEC-382) — a driver entry broke one of the hauler's rules and the phone didn't
  // settle it. Amber: a check, never a block on approve.
  VALIDATION_RULE: {
    label: 'Entry Rule',
    bg: 'rgba(246, 182, 94, 0.18)',
    fg: 'var(--color-warning)',
  },
  // #60 (DEC-365, D8) — informational: the timeline says from where to where and why.
  SITE_CHANGED: {
    label: 'Site Changed',
    bg: 'rgba(123, 150, 232, 0.15)',
    fg: 'var(--color-link)',
  },
};

export function FlagChip({ code }: { code: InReviewFlagCode }): React.JSX.Element {
  const meta = FLAG_META[code];
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        marginRight: '4px',
        marginBottom: '2px',
        fontSize: '11px',
        fontWeight: 600,
        borderRadius: 'var(--radius-pill)',
        backgroundColor: meta.bg,
        color: meta.fg,
        whiteSpace: 'nowrap',
      }}
    >
      {meta.label}
    </span>
  );
}

export function StaleBadge(): React.JSX.Element {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        fontSize: '11px',
        fontWeight: 700,
        borderRadius: '12px',
        backgroundColor: 'rgba(246, 182, 94, 0.22)',
        color: 'var(--color-warning)',
      }}
    >
      Stale
    </span>
  );
}
